import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import pluralize from 'pluralize';
import moment, { Moment } from 'moment';
import { useDidUpdate } from 'rooks';
import { RangeValue } from 'rc-picker/lib/interface';
import { isNil } from 'lodash';
import queryString from 'query-string';
import cx from 'classnames';
import { Button, DatePicker, Table } from 'antd';
import {
  ColumnsType,
  FilterValue,
  SorterResult,
  TablePaginationConfig,
} from 'antd/lib/table/interface';

import BasePageLabel from 'components/wrappers/BasePageLabel';
import BaseTableHeader from 'components/wrappers/BaseTableHeader';
import { DateParams } from 'utils/filterUtils';
import {
  disableSpecificNumberDates,
  getDateRangeTillLastSunday,
  getDateWithoutTime,
} from 'utils/dateUtils';
import useSortColumns, { ISortParams } from 'hooks/useSortColumns';
import { CustomerTrendWeeklyProfitSortFieldEnum } from '../enums/CustomerTrendWeeklyProfitSortField.enum';
import { SortingDirectionEnum } from 'enums';
import { pageDefaultSizeTable } from 'constants/index';
import { useAppDispatch } from 'hooks/useAppDispatch';
import {
  getCustomerTrendWeeklyProfitReport,
  getCustomerTrendWeeklyProfitReportExcel,
} from 'redux/reducers/homeDashboardSlice';
import { getSortOrderRequestValue } from 'utils/sortUtils';
import {
  getDynamicColumnsReportFormattedValues,
  mapDatesToColumnFormat,
} from 'utils/dynamicColumnsUtils';
import {
  TrendProfitReportItemViewModel,
  TrendProfitReportViewModel,
} from 'api/backofficeApiClient';
import LastSyncDateSection from 'components/LastSyncDateSection';
import { downloadFileByObjectURL } from 'utils/fileUtils';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';

import styles from './CustomerTrendWeeklyProfitReportDetailsPage.module.less';
import commonStyles from '../UserHomePage.module.less';

enum CustomerTrendWeeklyProfitReportFilterTypes {
  Date = 'date',
}

const dateRangeDurationInWeeks = 51;
const defaultDateRangeDurationInWeeks = 7;

type CustomerTrendWeeklyProfitReportFilters = {
  [CustomerTrendWeeklyProfitReportFilterTypes.Date]: DateParams | undefined;
};

const defaultDateFilter: DateParams = getDateRangeTillLastSunday(
  defaultDateRangeDurationInWeeks,
);

const defaultSortParams: ISortParams<CustomerTrendWeeklyProfitSortFieldEnum> = {
  sortOrder: 'descend',
  sortBy: CustomerTrendWeeklyProfitSortFieldEnum.Total,
};

export default function CustomerTrendWeeklyProfitReportDetailsPage() {
  const [
    isCustomerTrendWeeklyProfitReportDataLoading,
    setIsCustomerTrendWeeklyProfitReportDataLoading,
  ] = useState(false);
  const [
    customerTrendWeeklyProfitReportData,
    setCustomerTrendWeeklyProfitReportData,
  ] = useState<TrendProfitReportViewModel>();
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [selectedDate, setSelectedDate] = useState<DateParams | undefined>(
    undefined,
  );
  const [currentTablePage, setCurrentTablePage] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();
  const { sortBy, sortOrder, startDate, endDate } = queryString.parse(
    location.search,
  );
  const dispatch = useAppDispatch();
  const initialSortParams: ISortParams<CustomerTrendWeeklyProfitSortFieldEnum> =
    sortBy && sortOrder
      ? {
          sortBy: sortBy,
          sortOrder,
        }
      : defaultSortParams;
  const initialFilters = {
    [CustomerTrendWeeklyProfitReportFilterTypes.Date]:
      startDate && endDate
        ? {
            startDate: moment(startDate, 'MM/DD/YYYY'),
            endDate: moment(endDate, 'MM/DD/YYYY'),
          }
        : defaultDateFilter,
  };
  const [filters, setFilters] =
    useState<CustomerTrendWeeklyProfitReportFilters>(initialFilters);

  const customerTrendWeeklyProfitReportCountMessage = pluralize(
    'customer',
    customerTrendWeeklyProfitReportData?.totalCount || 0,
    true,
  );

  const columns = useMemo(() => {
    const columns: ColumnsType<TrendProfitReportItemViewModel> = [
      {
        title: 'Customer',
        key: CustomerTrendWeeklyProfitSortFieldEnum.CustomerName,
        fixed: 'left',
        render: (record: TrendProfitReportItemViewModel) => (
          <Link className="wrapped-text" to={`/customer/${record.customerKey}`}>
            {record.customerName}
          </Link>
        ),
      },
      {
        title: 'Total',
        key: CustomerTrendWeeklyProfitSortFieldEnum.Total,
        render: (record: TrendProfitReportItemViewModel) =>
          record.profitAmountTotal,
      },
      {
        title: '% of Total',
        key: CustomerTrendWeeklyProfitSortFieldEnum.TotalPercentage,
        render: (record: TrendProfitReportItemViewModel) =>
          record.percentageOfGreatTotal,
      },
    ];

    const sortedDates = mapDatesToColumnFormat(
      customerTrendWeeklyProfitReportData?.grandTotal?.sundayReportDetails ||
        {},
    );

    const columnsDate: ColumnsType<TrendProfitReportItemViewModel> =
      sortedDates.map((dateDetails) => {
        return {
          key: dateDetails.date,
          title: dateDetails.date,
          render: (record) => record[dateDetails.date],
        };
      });
    // Insert dynamic Sundays after the first column.
    columnsDate.length && columns.splice(1, 0, ...columnsDate);

    return columns;
  }, [customerTrendWeeklyProfitReportData]);

  const { sortColumns, sortParams, setSortParams } = useSortColumns(
    columns,
    initialSortParams,
  );

  useDidUpdate(() => {
    const queryData = {
      sortBy: sortParams.sortBy,
      sortOrder: sortParams.sortOrder,
      startDate: filters[CustomerTrendWeeklyProfitReportFilterTypes.Date]
        ?.startDate
        ? filters[
            CustomerTrendWeeklyProfitReportFilterTypes.Date
          ]?.startDate?.format('MM/DD/YYYY')
        : undefined,
      endDate: filters[CustomerTrendWeeklyProfitReportFilterTypes.Date]?.endDate
        ? filters[
            CustomerTrendWeeklyProfitReportFilterTypes.Date
          ]?.endDate?.format('MM/DD/YYYY')
        : undefined,
    };

    navigate(`${location.pathname}?${queryString.stringify(queryData)}`, {
      replace: true,
    });
    // eslint-disable-next-line
  }, [sortParams.sortBy, sortParams.sortOrder, filters]);

  const loadCustomerTrendWeeklyProfitReportData = useCallback(async () => {
    setIsCustomerTrendWeeklyProfitReportDataLoading(true);

    const result = await dispatch(
      getCustomerTrendWeeklyProfitReport({
        pageNumber: currentTablePage - 1,
        sortField: sortParams.sortBy,
        sortType: getSortOrderRequestValue(sortParams.sortOrder),
        startDate: filters[CustomerTrendWeeklyProfitReportFilterTypes.Date]
          ? getDateWithoutTime(
              filters[CustomerTrendWeeklyProfitReportFilterTypes.Date]
                ?.startDate,
            )
          : undefined,
        endDate: filters[CustomerTrendWeeklyProfitReportFilterTypes.Date]
          ? getDateWithoutTime(
              filters[CustomerTrendWeeklyProfitReportFilterTypes.Date]?.endDate,
            )
          : undefined,
        pageSize: pageDefaultSizeTable,
      }),
    );
    if (getCustomerTrendWeeklyProfitReport.fulfilled.match(result)) {
      setCustomerTrendWeeklyProfitReportData(
        getDynamicColumnsReportFormattedValues(result.payload),
      );
    } else {
      setCustomerTrendWeeklyProfitReportData({
        grandTotal: undefined,
        totalCount: 0,
        items: [],
      });
    }

    setIsCustomerTrendWeeklyProfitReportDataLoading(false);
  }, [
    dispatch,
    currentTablePage,
    sortParams.sortOrder,
    sortParams.sortBy,
    filters,
  ]);

  const loadInitialData = useCallback(async () => {
    await loadCustomerTrendWeeklyProfitReportData();

    setIsInitialDataLoaded(true);
  }, [loadCustomerTrendWeeklyProfitReportData]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const rangePickerValue = useMemo((): RangeValue<Moment> | undefined => {
    if (selectedDate) {
      return Object.entries(selectedDate).length
        ? [
            selectedDate.startDate
              ? moment(selectedDate.startDate, 'MM/DD/YYYY')
              : null,
            selectedDate.endDate
              ? moment(selectedDate.endDate, 'MM/DD/YYYY')
              : null,
          ]
        : undefined;
    }

    if (filters[CustomerTrendWeeklyProfitReportFilterTypes.Date]) {
      return [
        moment(
          filters[CustomerTrendWeeklyProfitReportFilterTypes.Date]?.startDate,
          'MM/DD/YYYY',
        ),
        moment(
          filters[CustomerTrendWeeklyProfitReportFilterTypes.Date]?.endDate,
          'MM/DD/YYYY',
        ),
      ];
    }
  }, [selectedDate, filters]);

  const handleCalendarChange = (dates: RangeValue<Moment>) => {
    if (dates) {
      setSelectedDate({
        startDate: dates[0],
        endDate: dates[1],
      });
    } else {
      setSelectedDate(undefined);
    }
  };

  const handleSelectDate =
    (type: CustomerTrendWeeklyProfitReportFilterTypes) =>
    (date: RangeValue<Moment>) => {
      if (date) {
        // Reset sort params by date column if
        // the date is not in the date picker's range
        if (
          !isNil(sortParams.sortBy) &&
          moment(sortParams.sortBy).isValid() &&
          !moment(sortParams.sortBy).isBetween(date[0], date[1], 'D', '[]')
        ) {
          setSortParams(defaultSortParams);
        }

        setFilters({
          ...filters,
          [type]: {
            startDate: date[0],
            endDate: date[1],
          },
        });
      } else {
        setFilters({
          ...filters,
          [type]: undefined,
        });
      }
      setCurrentTablePage(1);
    };

  const onOpenChange = (open: boolean) => {
    if (open) {
      setSelectedDate({});
    } else {
      setSelectedDate(undefined);
    }
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filter: Record<string, FilterValue | null>,
    sort:
      | SorterResult<TrendProfitReportItemViewModel>
      | SorterResult<TrendProfitReportItemViewModel>[],
  ) => {
    const sortOrder =
      (sort as SorterResult<TrendProfitReportItemViewModel>).order ||
      SortingDirectionEnum.Ascend;
    const sortBy = (sort as SorterResult<TrendProfitReportItemViewModel>)
      .columnKey as CustomerTrendWeeklyProfitSortFieldEnum;

    setSortParams(({ sortBy: prevSortBy, sortOrder: prevSortOrder }) => {
      if (prevSortBy !== sortBy || prevSortOrder !== sortOrder) {
        // Reset table page if sorting has changed
        setCurrentTablePage(1);
      } else {
        setCurrentTablePage(pagination.current || 1);
      }

      return {
        sortOrder,
        sortBy,
      };
    });
  };

  const handleDownloadReport = async () => {
    const result = await dispatch(
      getCustomerTrendWeeklyProfitReportExcel({
        pageNumber: currentTablePage - 1,
        sortField: sortParams.sortBy,
        sortType: getSortOrderRequestValue(sortParams.sortOrder),
        startDate: filters[CustomerTrendWeeklyProfitReportFilterTypes.Date]
          ? getDateWithoutTime(
              filters[CustomerTrendWeeklyProfitReportFilterTypes.Date]
                ?.startDate,
            )
          : undefined,
        endDate: filters[CustomerTrendWeeklyProfitReportFilterTypes.Date]
          ? getDateWithoutTime(
              filters[CustomerTrendWeeklyProfitReportFilterTypes.Date]?.endDate,
            )
          : undefined,
        pageSize: pageDefaultSizeTable,
      }),
    );

    if (getCustomerTrendWeeklyProfitReportExcel.fulfilled.match(result)) {
      downloadFileByObjectURL(result.payload.url, result.payload.fileName);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  return (
    <div className="page-wrapper scroll-box">
      <div className="page-holder">
        <BasePageLabel label="Dashboard details">
          {customerTrendWeeklyProfitReportData?.lastSyncDate && (
            <div className="title-line__info">
              <LastSyncDateSection
                syncDate={customerTrendWeeklyProfitReportData.lastSyncDate}
                title="Weekly Profit by Customer report is automatically refreshed every 15 minutes."
              />
            </div>
          )}
        </BasePageLabel>
        <div className="page-box page-inner">
          <div className="page-inner-container">
            <BaseTableHeader
              enableSubTitle={isInitialDataLoaded}
              subTitle={customerTrendWeeklyProfitReportCountMessage}
              title="Trend - Weekly Profit by Customer"
            >
              <div>
                <DatePicker.RangePicker
                  key="filterByDate"
                  format="MM/DD/YYYY"
                  separator="-"
                  className="d-flex"
                  allowClear={false}
                  onCalendarChange={handleCalendarChange as any}
                  value={rangePickerValue}
                  disabledDate={
                    disableSpecificNumberDates(
                      selectedDate?.startDate,
                      selectedDate?.endDate,
                      dateRangeDurationInWeeks,
                    ) as any
                  }
                  onOpenChange={onOpenChange}
                  onChange={
                    handleSelectDate(
                      CustomerTrendWeeklyProfitReportFilterTypes.Date,
                    ) as any
                  }
                />
              </div>

              <div>
                <Button
                  className={commonStyles.saveBtn}
                  onClick={handleDownloadReport}
                  icon={<span className="icon icon-xls-file" />}
                />
              </div>
            </BaseTableHeader>
            <Table
              className={cx(styles.table, 'custom-table')}
              pagination={{
                total: customerTrendWeeklyProfitReportData?.totalCount,
                pageSize: pageDefaultSizeTable,
                current: currentTablePage,
                showSizeChanger: false,
              }}
              showSorterTooltip={false}
              summary={() => (
                <Table.Summary fixed={true}>
                  <Table.Summary.Row>
                    <Table.Summary.Cell colSpan={1} index={0}>
                      Grand Total
                    </Table.Summary.Cell>
                    {customerTrendWeeklyProfitReportData?.grandTotal
                      ?.sundayReportDetails &&
                      mapDatesToColumnFormat(
                        customerTrendWeeklyProfitReportData?.grandTotal
                          ?.sundayReportDetails,
                      ).map((dateDetails, index: number) => (
                        <Table.Summary.Cell
                          colSpan={1}
                          index={2 + index++}
                          key={dateDetails.date}
                        >
                          {dateDetails.billing}
                        </Table.Summary.Cell>
                      ))}
                    <Table.Summary.Cell colSpan={1} index={1}>
                      {
                        customerTrendWeeklyProfitReportData?.grandTotal
                          ?.profitAmountTotal
                      }
                    </Table.Summary.Cell>
                    <Table.Summary.Cell colSpan={1} index={2}>
                      {
                        customerTrendWeeklyProfitReportData?.grandTotal
                          ?.percentageOfGreatTotal
                      }
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
              onChange={handleTableChange}
              columns={sortColumns}
              dataSource={customerTrendWeeklyProfitReportData?.items}
              rowKey={(record) => record?.customerKey as string}
              loading={isCustomerTrendWeeklyProfitReportDataLoading}
              scroll={{ x: '100%' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
